import React, { useState, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Franchise } from "../../Global Icons/Franchise.svg";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import TextInput from "../../Global Functions/textInput";
import TwoButtons from "../../Global Functions/buttons";
import { addRewardVoucher } from "../../Redux/Actions/rewardsAction";
import { useNavigate } from "react-router-dom";
import "./addReward.css";
import { FaTrash } from "react-icons/fa";

const CATEGORY_OPTIONS = [
  "Other's",
  "Electronics",
  "Luxury",
  "Travel",
  "Tech",
  "Lifestyle",
  "Food",
];

const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

const AddRewards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { countryList = { data: [] } } = useSelector((state) => state.country || {});

  const [formState, setFormState] = useState({
    voucher: "",
    title: "",
    description: "",
    reward_points: "",
    image: "",
    brochure: "",
    terms_and_conditions: [""],
    how_to_redeem: [""],
    category: "Other's",
    position: "",
    country: "",
  });

  const [imagePreview, setImagePreview] = useState(null);

  const imageInputRef = useRef(null);
  const brochureInputRef = useRef(null);

  const getCharCountClass = (length, max) => {
    if (length >= max) return "char-count error";
    if (length >= max * 0.8) return "char-count warning";
    return "char-count safe";
  };

  const getCharCountPercentage = (length, max) => {
    return Math.min((length / max) * 100, 100);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "title" && value.length > MAX_TITLE_LENGTH) return;
    if (name === "description" && value.length > MAX_DESCRIPTION_LENGTH) return;

    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayChange = (field, index, value) => {
    const updatedArray = [...formState[field]];
    updatedArray[index] = value;
    setFormState((prev) => ({
      ...prev,
      [field]: updatedArray,
    }));
  };

  const handleAddArrayItem = (field) => {
    setFormState((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    const updatedArray = [...formState[field]];
    if (updatedArray.length > 1) {
      updatedArray.splice(index, 1);
      setFormState((prev) => ({
        ...prev,
        [field]: updatedArray,
      }));
    } else {
      toast.error("At least one item is required.");
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload an image in PNG, JPEG, or JPG format only.");
      event.target.value = "";
      return;
    }

    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        if (event.target.name === "logo") {
          setFormState((prev) => ({ ...prev, logo: uploadedUrl }));
        } else if (event.target.name === "image") {
          setFormState((prev) => ({ ...prev, image: uploadedUrl }));
          setImagePreview(uploadedUrl);
        }
      }
    } catch {
      toast.error("Image upload failed");
    }
  };

  const handleBrochureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file only.");
      return;
    }

    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        setFormState((prev) => ({ ...prev, brochure: uploadedUrl }));
      }
    } catch {
      toast.error("PDF upload failed");
    }
  };

  const handleSubmit = async () => {
    const rewardPointsValue = Number(formState.reward_points);
    if (
      !formState.voucher ||
      !formState.title ||
      !formState.description ||
      !formState.reward_points ||
      !formState.category ||
      !formState.country
    ) {
      return toast.error("Please fill all required fields including category and country.");
    }

    if (formState.title.length > MAX_TITLE_LENGTH) {
      return toast.error(`Title cannot exceed ${MAX_TITLE_LENGTH} characters.`);
    }

    if (formState.description.length > MAX_DESCRIPTION_LENGTH) {
      return toast.error(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters.`);
    }

    if (!formState.image) {
      return toast.error("Image is required.");
    }

    if (!Number.isInteger(rewardPointsValue) || rewardPointsValue <= 0) {
      return toast.error(
        "Reward Points must be a positive integer (no decimals, no zero)."
      );
    }

    if (
      formState.terms_and_conditions.length === 0 ||
      formState.terms_and_conditions.every((t) => t.trim() === "")
    ) {
      return toast.error("Please add at least one Term & Condition.");
    }

    if (
      formState.how_to_redeem.length === 0 ||
      formState.how_to_redeem.every((t) => t.trim() === "")
    ) {
      return toast.error("Please add at least one How to Redeem step.");
    }

    const payload = {
      ...formState,
      reward_points: rewardPointsValue,
      country_id: formState.country,
      terms_and_conditions: formState.terms_and_conditions.filter(
        (t) => t.trim() !== ""
      ),
      how_to_redeem: formState.how_to_redeem.filter((t) => t.trim() !== ""),
    };

    try {
      const response = await dispatch(addRewardVoucher(payload));

      if (
        response &&
        (response.statusCode === 200 || response.status === "success")
      ) {
        toast.success("Reward voucher added successfully!");
        setFormState({
          voucher: "",
          title: "",
          description: "",
          reward_points: "",
          image: "",
          brochure: "",
          terms_and_conditions: [""],
          how_to_redeem: [""],
          category: "Other's",
          country: "",
          position: "",
        });
        setImagePreview(null);

        if (imageInputRef.current) imageInputRef.current.value = "";
        if (brochureInputRef.current) brochureInputRef.current.value = "";
        navigate("/rewards");
      } else {
        toast.error(response.message || "Failed to add reward voucher");
      }
    } catch (error) {
      toast.error("Error submitting reward voucher");
    }
  };

  const CharCountDisplay = ({ current, max, fieldName }) => {
    const percentage = getCharCountPercentage(current, max);
    const statusClass = getCharCountClass(current, max);
    const isWarning = current >= max * 0.8 && current < max;
    const isError = current >= max;

    return (
      <div className={`char-count-container ${statusClass}`}>
        <div className="char-count-bar">
          <div
            className="char-count-fill"
            style={{
              width: `${percentage}%`,
              backgroundColor:
                isError ? "#d9534f" : isWarning ? "#f0ad4e" : "#5cb85c",
            }}
          />
        </div>
        <div className="char-count-text">
          <span className="char-count-current">{current}</span>
          <span className="char-count-separator">/</span>
          <span className="char-count-max">{max}</span>
          {isWarning && !isError && <span className="char-count-warning-icon">⚠️</span>}
          {isError && <span className="char-count-error-icon">✕</span>}
        </div>
      </div>
    );
  };


  return (
    <MainLayout>
      <div className="franchise-form-container">
        <div className="franchise-form-wrapper franchise-card">
          <div className="franchise-card-content">
            <div className="franchise-header">
              <div className="franchise-header-icon">
                <Franchise />
              </div>
              <h1>Add Reward Voucher</h1>
            </div>

            {/* Country Select */}
            <div className="form-sections">
              <label className="form-label" htmlFor="country_select">
                Country <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="country_select"
                name="country"
                value={formState.country}
                onChange={handleChange}
                className="form-input"
              // style={{ width: "94.2%" }}
              >
                <option value="" disabled>
                  Select Country
                </option>
                {countryList.data.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.country_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-sections">
              <TextInput
                label="Voucher"
                name="voucher"
                value={formState.voucher}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
              />
            </div>

            <div className="form-sections">
              <TextInput
                label="Title"
                name="title"
                value={formState.title}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
                maxLength={MAX_TITLE_LENGTH}
              />
              <CharCountDisplay
                current={formState.title.length}
                max={MAX_TITLE_LENGTH}
                fieldName="Title"
              />
            </div>

            <div className="form-sections">
              <TextInput
                label="Description"
                name="description"
                value={formState.description}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
              <CharCountDisplay
                current={formState.description.length}
                max={MAX_DESCRIPTION_LENGTH}
                fieldName="Description"
              />
            </div>

            <TextInput
              label="Reward Points"
              name="reward_points"
              type="number"
              value={formState.reward_points}
              required
              inputClass="form-input"
              labelClass="form-label"
              min={1}
              step={1}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  reward_points: e.target.value,
                }))
              }
            />


            {/* Category Select */}
            <div
              className="form-sections form-grid-2"
              style={{ marginTop: "-24px" }}
            >
              {/* Category Dropdown */}
              <div>
                <label className="form-label" htmlFor="category_select">
                  Category <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  style={{ width: "94.2%" }}
                  id="category_select"
                  name="category"
                  value={formState.category}
                  onChange={handleChange}
                  className="form-input"
                >
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Position Field */}
              <div>
                <label className="form-label" htmlFor="position_input">
                  Position
                </label>
                <input
                  style={{ width: "94.2%" }}
                  id="position_input"
                  name="position"
                  type="number"
                  value={formState.position}
                  onChange={handleChange}
                  className="form-input"
                  required
                  min={1}
                  inputMode="numeric"
                  pattern="^[1-9][0-9]*$"
                  onInput={(e) => {
                    let value = e.target.value.replace(/[^0-9]/g, "");
                    value = value.replace(/^0+/, "");
                    setFormState((prev) => ({
                      ...prev,
                      position: value,
                    }));
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "27%", flexWrap: "wrap" }}>
              {/* Terms and Conditions Section */}
              <div className="form-sections">
                <label className="form-label">
                  Terms & Conditions <span style={{ color: "red" }}>*</span>
                </label>
                {formState.terms_and_conditions.map((term, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <TextInput
                      value={term}
                      onChange={(e) =>
                        handleArrayChange(
                          "terms_and_conditions",
                          index,
                          e.target.value
                        )
                      }
                      inputClass="form-input"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveArrayItem("terms_and_conditions", index)
                      }
                      style={{
                        marginLeft: 8,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#d9534f",
                      }}
                      aria-label="Remove term"
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem("terms_and_conditions")}
                >
                  Add Term
                </button>
              </div>

              {/* How to Redeem Section */}
              <div className="form-sections">
                <label className="form-label">
                  How to Redeem <span style={{ color: "red" }}>*</span>
                </label>
                {formState.how_to_redeem.map((step, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <TextInput
                      value={step}
                      onChange={(e) =>
                        handleArrayChange(
                          "how_to_redeem",
                          index,
                          e.target.value
                        )
                      }
                      inputClass="form-input"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveArrayItem("how_to_redeem", index)
                      }
                      style={{
                        marginLeft: 8,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#d9534f",
                      }}
                      aria-label="Remove step"
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem("how_to_redeem")}
                >
                  Add Step
                </button>
              </div>
            </div>

            <div className="form-sections">
              <label
                className="form-label file-upload-area"
                htmlFor="image_input"
              >
                Image<span style={{ color: "red" }}>*</span>
              </label>
              <input
                id="image_input"
                style={{ width: "97%" }}
                type="file"
                name="image"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileChange}
                className="form-input"
                ref={imageInputRef}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Reward voucher preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "150px",
                    marginTop: 10,
                  }}
                />
              )}
            </div>

            {/* Brochure PDF upload */}
            <div className="form-sections">
              <label className="form-label" htmlFor="brochure_input">
                Brochure (PDF only)
              </label>
              <input
                style={{ width: "97%" }}
                id="brochure_input"
                type="file"
                name="brochure"
                accept="application/pdf"
                onChange={handleBrochureChange}
                className="form-input"
                ref={brochureInputRef}
              />
              {formState.brochure && (
                <a
                  href={formState.brochure}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block", marginTop: 8 }}
                >
                  View uploaded brochure
                </a>
              )}
            </div>

            <div className="form-navigation">
              <button type="button" onClick={() => navigate("/rewards")}>
                Cancel
              </button>
              <TwoButtons onClick2={handleSubmit} buttonHead2="Save" />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddRewards;
